import { ChartSetList } from '../types';
import { FreemiumMessageKey } from '../utils/freemiumLimits';

interface GigNavigationDeps {
  setLists: ChartSetList[];
  gigSetListId: string | null;
  setGigSetListId: (id: string | null) => void;
  gigCurrentSongIndex: number;
  setGigCurrentSongIndex: (index: number) => void;
  jamSessionState: string;
  jamSessionRole: string | null;
  uiSettings: { isDebugOverlayVisible?: boolean };
  userProfile: any;
  isGapiReady: boolean;
  subscription: { isPremium: boolean };
  handleLoadChart: (title: string, isFromNavigation?: boolean) => Promise<void>;
  fullSync: () => Promise<void>;
  loadSavedChartTitles: () => Promise<void>;
  loadSetLists: () => Promise<void>;
  setLoadingChartMessage: (msg: string) => void;
  setIsLoadingChart: (loading: boolean) => void;
  setFeatureLimitMessageKey: (key: FreemiumMessageKey | undefined) => void;
  setFeatureLimitModalOpen: (open: boolean) => void;
  modals: {
    closeGigSetListSelector: () => void;
    openLoadModal: () => void;
    openGigSetListSelector: () => void;
  };
}

export function useGigNavigation(deps: GigNavigationDeps) {
  const {
    setLists, gigSetListId, setGigSetListId, gigCurrentSongIndex, setGigCurrentSongIndex,
    jamSessionState, jamSessionRole, uiSettings,
    userProfile, isGapiReady, subscription,
    handleLoadChart, fullSync, loadSavedChartTitles, loadSetLists,
    setLoadingChartMessage, setIsLoadingChart,
    setFeatureLimitMessageKey, setFeatureLimitModalOpen,
    modals,
  } = deps;

  const selectGigSetList = (setListId: string) => {
    setGigSetListId(setListId);
    setGigCurrentSongIndex(0);
    modals.closeGigSetListSelector();

    const setList = setLists.find(sl => sl.id === setListId);
    if (setList && setList.chartTitles.length > 0) {
      const firstSong = setList.chartTitles[0];
      handleLoadChart(firstSong, true);
    }
  };

  const navigateGigSong = (direction: 'prev' | 'next') => {
    if (!gigSetListId) return;

    if (jamSessionState !== 'idle' && jamSessionRole === 'client') {
      console.log('Client cannot navigate setlist during jam session - only host controls chart changes');
      return;
    }

    const setList = setLists.find(sl => sl.id === gigSetListId);
    if (!setList || setList.chartTitles.length === 0) return;

    const currentIndex = gigCurrentSongIndex;
    let newIndex: number;

    if (direction === 'next') {
      newIndex = Math.min(currentIndex + 1, setList.chartTitles.length - 1);
    } else {
      newIndex = Math.max(currentIndex - 1, 0);
    }

    if (newIndex !== currentIndex) {
      console.log(`DEBUG: Moving from index ${currentIndex} to ${newIndex}, debug overlay was:`, uiSettings.isDebugOverlayVisible);
      setGigCurrentSongIndex(newIndex);
      const songTitle = setList.chartTitles[newIndex];
      handleLoadChart(songTitle, true);
      console.log(`DEBUG: After move to index ${newIndex}, debug overlay is:`, uiSettings.isDebugOverlayVisible);
    }
  };

  const clearGigSetList = () => {
    setGigSetListId(null);
    setGigCurrentSongIndex(0);
  };

  const handleOpenChartFromGig = async () => {
    setLoadingChartMessage('Loading charts...');
    setIsLoadingChart(true);

    try {
      if (userProfile && isGapiReady) {
        await fullSync();
      }
      await loadSavedChartTitles();
      modals.openLoadModal();
    } finally {
      setIsLoadingChart(false);
    }
  };

  const handleOpenSetlistFromGig = async () => {
    if (!subscription.isPremium) {
      setFeatureLimitMessageKey('GIG_MODE_SETLISTS_BLOCKED');
      setFeatureLimitModalOpen(true);
      return;
    }
    await loadSetLists();
    await loadSavedChartTitles();
    modals.openGigSetListSelector();
  };

  const handleNavigateSongFromGig = (direction: 'prev' | 'next') => {
    navigateGigSong(direction);
  };

  return {
    selectGigSetList,
    navigateGigSong,
    clearGigSetList,
    handleOpenChartFromGig,
    handleOpenSetlistFromGig,
    handleNavigateSongFromGig,
  };
}

export type UseGigNavigationReturn = ReturnType<typeof useGigNavigation>;
